import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {DialogComponent} from "@syncfusion/ej2-angular-popups";
import {ButtonPropsModel} from "@syncfusion/ej2-popups/src/dialog/dialog-model";
import {PortalPageComponent} from "../portal-page.component";
import {PortalModalOptions} from "./portal-modal-options";
import {PortalModalResult} from './portal-modal-result';
import {PortalService} from "../../../core/services/portal/portal.service";
import {$translateKey} from "../../../core/services/internationalization/localize.service";

type CloseCallbackFunc = (closeReason: string, data?: any) => void;

@Component({
  selector: 'sb-portal-modal',
  templateUrl: './portal-modal.component.html',
  styleUrls: ['./portal-modal.component.scss']
})
export class PortalModalComponent implements OnInit {

  // Set if we are displaying a single portal component in a modal.
  @Input()
  portalModalComponent: string;

  @Input()
  portalModalComponentParams: any;

  // Set if we are displaying a portal page in a modal.
  @Input()
  portalModalPage: string;

  // Showing buttons, etc
  @Input()
  portalModalOptions: PortalModalOptions;

  @ViewChild('dialog', {static: false})
  dialog: DialogComponent;

  @ViewChild('portal', {static: false})
  portal: PortalPageComponent;

  public visible = false;

  public position: any = { X: 'center', Y: 'center' };

  public buttons: ButtonPropsModel[] = null;

  @Input()
  title: string = "";

  @Input()
  closeCallback?: CloseCallbackFunc;

  closeDialogInvoked = false;

  minHeight = 600;

  height = 600;

  width = 900;

  showCloseIcon = true;

  closeOnEscape = true;

  cssClass = "";

  // These three inputs are so we can call methods on the angular component when
  // it is being used as a web component on the Knockout side.
  @Input()
  get ngShowDialog() {
    return this.showDialog();
  }

  @Input()
  get ngHideDialog() {
    return this.hideDialog();
  }

  @Input()
  set ngHideWithResult(result: PortalModalResult) {
    this.closeDialog({
      closedBy: result.closureReason,
      data: result.data
    });
  }

  @Input()
  get ngDestroyDialog() {
    return this.destroyDialog();
  }

  constructor(private portalService: PortalService) { }

  ngOnInit(): void {
  }

  public closeDialog(args: any) {
    // Just return if we have already invoked this method.
    if (this.closeDialogInvoked === true) { return; }

    this.visible = false;
    this.closeDialogInvoked = true;
    this.resetDimensions();

    let closeReason = '';
    if (args.closedBy === 'user action') {
      closeReason = "button";
    } else if (args.closedBy === 'close icon') {
      closeReason = "icon";
    } else if (args.closedBy === 'escape') {
      closeReason = "escape";
    } else {
      closeReason = args.closedBy;
    }

    if (this.closeCallback !== undefined) {
      this.closeCallback(closeReason, args.data);
    }
  }

  public async showDialog() {

    this.closeDialogInvoked = false;

    if (this?.portalModalOptions?.showCloseIcon != null) {
      this.showCloseIcon = this.portalModalOptions?.showCloseIcon;
    }

    if (this?.portalModalOptions?.closeOnEscape != null) {
      this.closeOnEscape = this.portalModalOptions?.closeOnEscape;
    }

    if (this?.portalModalOptions?.title) {
      this.title = this.portalModalOptions?.title;
    }

    if (this?.portalModalOptions?.cssClass) {
      this.cssClass = this.portalModalOptions?.cssClass;
    }

    if (this?.portalModalOptions?.closeCallback) {
      this.closeCallback = this.portalModalOptions?.closeCallback;
    }

    if (this?.portalModalOptions?.showCloseButton) {
      const that = this;
      this.buttons = [
        {
          'click': async (e) => {
            let hide = true;
            const comp = this.portalService.getComponentsForPage(this.portal)?.[0];
            if (comp.click) {
              const args = {
                event: e,
                modal: that,
                button: that.buttons[0]
              }
              let result = (await comp.click(args)) as any;
              hide = typeof result === 'boolean' ? result === true : hide;
            }
            if (hide) {
              this.hideDialog();
            }
          },
          buttonModel: {
            content: $translateKey('common.button.close'),
            isPrimary: true,
          }
        }
      ];
    }

    if (this?.portalModalOptions?.modalButtons) {
      const that = this;
      const wrappedButtons = this?.portalModalOptions?.modalButtons.map((b) => {
        return {
          'click': async (e) => {
            let hide = true;
            const comp = this.portalService.getComponentsForPage(this.portal)?.[0];
            if (b.click) {
              const params = comp?.parameters || {};
              const args = {
                event: e,
                modal: that,
                button: b,
                params: params
              };
              let result = await b.click(args) as any;
              hide = typeof result === 'boolean' ? result === true : hide;
            } else if (comp.click) {
              const args = {
                event: e,
                modal: that,
                button: b
              }
              let result = (await comp.click(args)) as any;
              hide = typeof result === 'boolean' ? result === true : hide;
            }
            if (hide) {
              this.hideDialog();
            }
          },
          buttonModel: b.buttonModel || {},
          isFlat: typeof b.isFlat === 'boolean' ? b.isFlat : true,
          type: b.type || 'Button'
        };
      });

      this.buttons = wrappedButtons;
    }

    this.dialog.show();
    this.visible = true;
  }

  public hideDialog() {
    this.dialog.hide();
    this.visible = false;
    this.resetDimensions();
  }

  public destroyDialog() {
    this.dialog.destroy();
    this.visible = false;
    this.resetDimensions();
  }

  public onTitle($event) {
    this.title = $event;
  }

  resetDimensions() {
    this.minHeight = 600;
    this.height = 600;
    this.width = 900;
  }

}
